/**
 * @file imageDragEvent.ts
 * @description Event system for dragging images between GenerationPanel slots.
 * Supports dragging images between Start Frame, End Frame, Image References, and Subjects.
 *
 * Uses module-level singleton pattern (like frameDragEvent.ts) for consistency.
 *
 * Features:
 * - Move (default): removes from source, adds to target
 * - Duplicate (Option/Alt held): keeps in source, adds copy to target
 */

/**
 * Source location of a dragged image
 */
export type ImageDragSource =
  | { type: 'startFrame' }
  | { type: 'endFrame' }
  | { type: 'imageReference'; index: number }
  | { type: 'setImage' }
  | { type: 'subject'; index: number }
  /** Drop-only: append the image as a reference view on the element at `index` (Kling card view box). */
  | { type: 'subjectView'; index: number }
  | { type: 'sourceVideoFrame'; assetId?: string; timestampSec?: number } // Frame grabbed from source video scrubber (read-only, always copy)
  // Library shelf still (read-only, always copy). Carries the library identity
  // so drops can route as Element (plain) / Ref image (⇧) and capture usage.
  | {
      type: 'library'
      libraryRef?: { collectionId: string; assetId?: string; versionId?: string }
      collectionName?: string
      assetName?: string
      /** True for collection-CARD drags (hero stands in) — drops bring the whole subject. */
      wholeCollection?: boolean
    }

/**
 * State for an image being dragged within GenerationPanel
 */
export interface ImageDragState {
  /** Data URL of the dragged image */
  imageUrl: string
  /** Where the image is being dragged from */
  source: ImageDragSource
  /** Current mouse X position */
  mouseX: number
  /** Current mouse Y position */
  mouseY: number
  /** Whether Option/Alt key is held (duplicate mode) */
  isOptionHeld: boolean
  /** X offset from left edge of preview where user grabbed (for positioning) */
  grabOffsetX: number
  /** Y offset from top edge of preview where user grabbed (for positioning) */
  grabOffsetY: number
  /** Canvas asset ID of the source image (for asset linkage). Only set for images from canvas assets. */
  canvasAssetId?: string
}

/**
 * Module-level storage for drag state
 */
let imageDragState: ImageDragState | null = null

/**
 * Listeners for state changes (used by React components to re-render)
 */
const listeners = new Set<() => void>()
const activeStateListeners = new Set<(isActive: boolean) => void>()

/**
 * Notify all listeners of state change
 */
function notifyListeners(): void {
  listeners.forEach(listener => listener())
}

function notifyActiveStateListeners(isActive: boolean): void {
  activeStateListeners.forEach(listener => listener(isActive))
}

/**
 * Subscribe to state changes
 * @returns Unsubscribe function
 */
export function subscribeToImageDrag(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Subscribe only to image-drag active/inactive transitions.
 * Avoids mousemove-frequency notifications for components that only
 * care whether a drag exists, not its live coordinates.
 */
export function subscribeToImageDragActive(listener: (isActive: boolean) => void): () => void {
  activeStateListeners.add(listener)
  return () => activeStateListeners.delete(listener)
}

/**
 * Start an image drag operation
 */
export function setImageDrag(state: ImageDragState): void {
  const wasActive = imageDragState !== null
  imageDragState = state
  notifyListeners()
  if (!wasActive) {
    notifyActiveStateListeners(true)
  }
}

/**
 * Update mouse position and modifier state during drag
 */
export function updateImageDrag(mouseX: number, mouseY: number, isOptionHeld: boolean): void {
  if (imageDragState) {
    imageDragState = {
      ...imageDragState,
      mouseX,
      mouseY,
      isOptionHeld,
    }
    notifyListeners()
  }
}

/**
 * Get current drag state without clearing
 */
export function getImageDrag(): ImageDragState | null {
  return imageDragState
}

/**
 * Get and clear the drag state (consume on drop)
 */
export function consumeImageDrag(): ImageDragState | null {
  const state = imageDragState
  const wasActive = state !== null
  imageDragState = null
  notifyListeners()
  if (wasActive) {
    notifyActiveStateListeners(false)
  }
  return state
}

/**
 * Check if there's an active image drag
 */
export function hasImageDrag(): boolean {
  return imageDragState !== null
}

/**
 * Clear drag state without returning it (e.g., on cancel)
 */
export function clearImageDrag(): void {
  const wasActive = imageDragState !== null
  imageDragState = null
  notifyListeners()
  if (wasActive) {
    notifyActiveStateListeners(false)
  }
}

/**
 * Check if two drag sources are the same location
 */
export function sourceEquals(a: ImageDragSource, b: ImageDragSource): boolean {
  if (a.type !== b.type) return false
  if (a.type === 'imageReference' && b.type === 'imageReference') {
    return a.index === b.index
  }
  if (a.type === 'subject' && b.type === 'subject') {
    return a.index === b.index
  }
  if (a.type === 'subjectView' && b.type === 'subjectView') {
    return a.index === b.index
  }
  if (a.type === 'setImage') return true
  // sourceVideoFrame/library are never equal to themselves (can always drop anywhere)
  if (a.type === 'sourceVideoFrame' || a.type === 'library') return false
  return true // startFrame === startFrame, endFrame === endFrame
}

/**
 * Check if a source is read-only (no removal on move)
 */
export function isReadOnlySource(source: ImageDragSource): boolean {
  return source.type === 'sourceVideoFrame' || source.type === 'setImage' || source.type === 'library'
}

/**
 * Check if the current drag can be dropped on a target
 * Returns true if there's an active drag and it's not from the same source
 */
export function isValidDropTarget(target: ImageDragSource): boolean {
  if (!imageDragState) return false
  return !sourceEquals(imageDragState.source, target)
}
